import { api } from '@agency/shared';
import { Chip, DataTable, PageHeader, StateBadge, type DataTableColumn } from '@agency/ui';
import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { ErrorCallout, IdCell, Page, Stamp, TableCard, UserLink } from '../../app/widgets.js';
import { ApiError, call } from '../../lib/api.js';
import { HEALTH_STATE } from '../../lib/labels.js';
import { useApi } from '../../lib/useApi.js';

type Row = api.Connection & { accountName: string };

/** All registered connections (aggregated per registry account). */
export function ConnectionsPage() {
  const accounts = useApi('admin.accounts.list', { input: { limit: 200 } });
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  useEffect(() => {
    if (!accounts.data) return;
    let cancelled = false;
    const withConnections = accounts.data.items.filter((a) => a.connectionCount > 0);
    void (async () => {
      try {
        const results = await Promise.all(withConnections.map(async (a) => (await call('admin.accounts.connections', { params: { accountId: a.id } })).data.items.map((c) => ({ ...c, accountName: a.displayName }))));
        if (!cancelled) {
          setRows(results.flat().filter((c) => !c.removedAt));
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(ApiError.from(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accounts.data]);

  const columns = useMemo<DataTableColumn<Row>[]>(
    () => [
      { id: 'account', header: 'Account', cell: ({ row }) => <Link to="/accounts/$accountId" params={{ accountId: row.original.accountId }} className="hover:underline">{row.original.accountName}</Link>, size: 220 },
      { id: 'provider', header: 'Provider', cell: ({ row }) => <Chip tone="info">{row.original.provider}</Chip>, size: 90 },
      { id: 'user', header: 'Operator', cell: ({ row }) => <UserLink userId={row.original.userId} />, size: 160 },
      { id: 'device', header: 'Device', cell: ({ row }) => <IdCell value={row.original.deviceId} />, size: 120 },
      { id: 'fingerprint', header: 'Fingerprint', cell: ({ row }) => <span className="font-mono text-xs-plus text-mute">{row.original.fingerprint.slice(0, 16)}…</span>, size: 170 },
      { id: 'health', header: 'Health', cell: ({ row }) => <StateBadge state={HEALTH_STATE[row.original.health]} />, size: 130 },
      { id: 'sync', header: 'Last sync', cell: ({ row }) => <span className="flex items-center gap-2"><Stamp value={row.original.lastSyncAt} relative />{row.original.lastSyncState ? <span className="font-mono text-2xs uppercase text-dim">{row.original.lastSyncState}</span> : null}</span>, size: 180 },
      { id: 'registered', header: 'Registered', cell: ({ row }) => <Stamp value={row.original.registeredAt} />, size: 150 },
    ],
    [],
  );
  const list = rows ?? [];
  return (
    <Page>
      <PageHeader eyebrow="Accounts and devices" eyebrowPath="Connections" title="Connections" subtitle={rows ? `${list.length} provider connections across ${new Set(list.map((c) => c.accountId)).size} accounts · device reported` : 'Loading connection metadata.'} />
      <ErrorCallout error={accounts.error ?? error} onRetry={() => void accounts.reload()} className="mt-6" />
      <div className="mt-6">
        <TableCard title="Registered connections" subtitle="Metadata only · fingerprints are opaque">
          <DataTable<Row> aria-label="Connections" columns={columns} rows={list} rowKey={(r) => r.id} height={Math.min(720, Math.max(200, list.length * 34 + 40))} state={rows === null ? 'loading' : list.length === 0 ? 'empty' : 'complete'} emptyMessage="No device has registered a provider connection yet." noun="connections" />
        </TableCard>
      </div>
    </Page>
  );
}
